import { useState, type FormEvent } from 'react';
import { Button, FormField, Input } from '@/shared/components';
import { AuthAlert } from './AuthAlert';
import { twoFactorFailure, type AuthFailure } from './loginFailure';

interface Props {
  onBack: () => void;
  onComplete: (code: string, useRecoveryCode: boolean) => Promise<void>;
}

/** Zweite Stufe: Code aus der App oder ein Wiederherstellungscode; Fehler als Karte über dem Feld. */
export function LoginTwoFactorForm({ onBack, onComplete }: Props) {
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<AuthFailure | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setFailure(null);
    try {
      await onComplete(code, useRecoveryCode);
    } catch (error) {
      const failure = await twoFactorFailure(error, 'code');
      setFailure(
        failure.unknown && useRecoveryCode
          ? {
              ...failure,
              text: `${failure.text} Der Wiederherstellungscode ist dann womöglich verbraucht – nimm einen anderen oder melde dich neu an.`,
            }
          : failure,
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-[18px]" noValidate>
      {failure && <AuthAlert title={failure.title} text={failure.text} />}
      <FormField
        label={useRecoveryCode ? 'Wiederherstellungscode' : '6-stelliger Code'}
        hint={
          useRecoveryCode ? 'Jeder Code funktioniert nur einmal.' : 'Aus deiner Authenticator-App.'
        }
        required
      >
        <Input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          inputMode={useRecoveryCode ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          className="font-mono tracking-[0.2em]"
          autoFocus
        />
      </FormField>
      <button
        type="button"
        className="text-forest inline-flex min-h-11 items-center self-start text-sm font-semibold underline-offset-4 hover:underline"
        onClick={() => {
          setUseRecoveryCode((value) => !value);
          setCode('');
          setFailure(null);
        }}
      >
        {useRecoveryCode ? 'Code aus der App verwenden' : 'Wiederherstellungscode verwenden'}
      </button>
      <Button
        type="submit"
        variant="brand"
        size="lg"
        className="h-[54px] w-full text-base"
        loading={submitting}
        disabled={!code.trim()}
      >
        Anmeldung abschließen
      </Button>
      <Button type="button" variant="ghost" size="lg" onClick={onBack}>
        Zurück zur Anmeldung
      </Button>
    </form>
  );
}
